import React, { useEffect, useState, useRef } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, TextField, CircularProgress,
    IconButton, Chip, Stack, InputAdornment, Avatar, Dialog,
    DialogTitle, DialogContent, Button, MenuItem, Tooltip
} from "@mui/material";
import Webcam from "react-webcam";
import {
    Visibility, Edit, Search, PhotoCamera, FileUpload, Replay, DeleteForever
} from "@mui/icons-material";
import { 
    collection, getDocs, doc, query, where, getDoc, updateDoc, 
    orderBy, deleteDoc, addDoc, serverTimestamp 
} from "firebase/firestore";
import { ref, uploadString, getDownloadURL, uploadBytes } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth, storage } from "../../firebase";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function Cancellist() {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedClass, setSelectedClass] = useState("All");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [context, setContext] = useState({ schoolId: null, activeYear: null });

    const [photoDialog, setPhotoDialog] = useState({ open: false, student: null });
    const [isWebcam, setIsWebcam] = useState(false);
    const [uploading, setUploading] = useState(false);
    const webcamRef = useRef(null);

    useEffect(() => {
        // Wait for Firebase Auth state to resolve reliably
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                setLoading(false);
                return;
            }

            try {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                const sId = userDoc.data()?.schoolId;

                if (!sId) {
                    setLoading(false);
                    return;
                }

                // Query active academic year
                const yearSnap = await getDocs(
                    query(
                        collection(db, "academicyears"),
                        where("schoolId", "==", sId),
                        where("status", "==", "Active")
                    )
                );

                const activeYear = yearSnap.empty ? null : yearSnap.docs[0].data().name;
                setContext({ schoolId: sId, activeYear });

                if (sId && activeYear) {
                    await fetchData(sId, activeYear);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Initialization error:", error);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const fetchData = async (sId, year) => {
        try {
            setLoading(true);

            // Fetch courses with fallback in case of index/ordering issues
            let courseSnap;
            try {
                courseSnap = await getDocs(query(collection(db, "courses"), orderBy("sequence", "asc")));
            } catch (e) {
                courseSnap = await getDocs(collection(db, "courses"));
            }

            const [admSnap, stdSnap] = await Promise.all([
                getDocs(
                    query(
                        collection(db, "admission"),
                        where("schoolId", "==", sId),
                        where("academicYear", "==", year),
                        where("status", "==", "Cancelled")
                    )
                ),
                getDocs(collection(db, "students"))
            ]);

            const courseMap = {};
            const classList = [];
            courseSnap.forEach((d) => {
                const data = d.data();
                courseMap[d.id] = data.name;
                classList.push({ id: d.id, name: data.name });
            });
            setClasses(classList);

            const admMap = {};
            admSnap.forEach((d) => {
                const data = d.data();
                if (data.studentId) {
                    admMap[data.studentId] = { ...data, admissionDocId: d.id };
                }
            });

            const merged = stdSnap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((s) => admMap[s.id])
                .map((s) => {
                    const adm = admMap[s.id] || {};
                    return {
                        ...s,
                        admissionDocId: adm.admissionDocId,
                        classId: adm.classId || adm.class,
                        className: courseMap[adm.classId || adm.class] || "N/A",
                        rollNo: adm.rollno || s.rollno || "-",
                        studenttype: adm.studenttype || s.studenttype || "Regular",
                        fatherMobile:
                            adm.father_mobile1 ||
                            adm.father_mobile ||
                            s.fatherMobile ||
                            s.father_mobile ||
                            "-",
                        mobile: s.mobile || adm.mobile || ""
                    };
                });

            setStudents(merged);
            setFilteredStudents(merged);
        } catch (e) {
            console.error("Data loading error:", e);
            Swal.fire("Error", "Failed to load cancelled admissions", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreAdmission = async (student) => {
        const result = await Swal.fire({
            title: "Restore Admission?",
            text: `Do you want to restore this student's admission?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Restore!",
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const admRef = doc(db, "admission", student.admissionDocId);
                    await updateDoc(admRef, { status: "Active" });
                    return true;
                } catch (error) {
                    Swal.showValidationMessage(`Request failed: ${error.message}`);
                }
            }
        });

        if (result.isConfirmed) {
            Swal.fire("Restored!", "Admission status updated to Active.", "success");
            const updated = students.filter((s) => s.id !== student.id);
            setStudents(updated);
            setFilteredStudents(updated);
        }
    };

    const handleCancelAdmission = async (student) => {
        try {
            const result = await Swal.fire({
                title: "Cancel Admission?",
                text: `Are you sure you want to cancel ${student?.firstname || student?.name || "this student"}'s admission?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Cancel it!",
                showLoaderOnConfirm: true,
                preConfirm: async () => {
                    try {
                        if (student?.admissionDocId) {
                            const admRef = doc(db, "admission", student.admissionDocId);
                            await updateDoc(admRef, { status: "Cancelled" });
                        } else if (student?.id) {
                            const studentRef = doc(db, "students", student.id);
                            await deleteDoc(studentRef);
                        } else {
                            throw new Error("No valid document ID found for this student.");
                        }
                        return true;
                    } catch (error) {
                        Swal.showValidationMessage(`Request failed: ${error.message}`);
                    }
                },
                allowOutsideClick: () => !Swal.isLoading()
            });

            if (result.isConfirmed) {
                Swal.fire("Cancelled!", "Record has been updated to cancelled.", "success");
                const updated = students.filter((s) => s.id !== student.id);
                setStudents(updated);
                setFilteredStudents(updated);
            }
        } catch (error) {
            console.error("Error cancelling admission:", error);
        }
    };

    const handlePermanentDelete = async (student) => {
        const result = await Swal.fire({
            title: "Permanently Delete?",
            text: `This action cannot be undone! This will permanently remove all data for ${student.firstname || "the student"}.`,
            icon: "error",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, Delete Permanently!",
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const studentId = student.id;
                    const admissionDocId = student.admissionDocId;

                    if (admissionDocId) {
                        await deleteDoc(doc(db, "admission", admissionDocId));
                    }
                    await deleteDoc(doc(db, "students", studentId));

                    await addDoc(collection(db, "logs"), {
                        action: "PERMANENT_DELETE_STUDENT",
                        studentId: studentId,
                        studentName: `${student.firstname || ""} ${student.surname || ""}`.trim(),
                        performedBy: auth.currentUser?.uid || "unknown",
                        timestamp: serverTimestamp(),
                        schoolId: context.schoolId || null
                    });

                    return true;
                } catch (error) {
                    Swal.showValidationMessage(`Deletion failed: ${error.message}`);
                }
            }
        });

        if (result.isConfirmed) {
            Swal.fire("Deleted!", "Student record has been permanently deleted.", "success");
            const updated = students.filter((s) => s.id !== student.id);
            setStudents(updated);
            setFilteredStudents(updated);
        }
    };

    const handleFilter = (txt, cls) => {
        setSearch(txt);
        setSelectedClass(cls);

        const term = txt.toLowerCase().trim();

        const filtered = students.filter((s) => {
            const fullName = `${s.firstname || s.name || ""} ${s.fathername || ""} ${s.surname || ""}`.toLowerCase();

            return (
                (cls === "All" || s.classId === cls) &&
                (!term ||
                    fullName.includes(term) ||
                    (s.grNo || "").toString().toLowerCase().includes(term) ||
                    (s.rollNo || "").toString().toLowerCase().includes(term) ||
                    (s.fatherMobile || "").toString().includes(term) ||
                    (s.mobile || "").toString().includes(term))
            );
        });

        setFilteredStudents(filtered);
        setPage(0);
    };

    const uploadPhoto = async (data, type) => {
        setUploading(true);
        try {
            const sRef = ref(storage, `students/${photoDialog.student.id}.jpg`);
            type === "url"
                ? await uploadString(sRef, data, "data_url")
                : await uploadBytes(sRef, data);
            const url = await getDownloadURL(sRef);
            await updateDoc(doc(db, "students", photoDialog.student.id), { photoURL: url });

            const updater = (list) =>
                list.map((s) => (s.id === photoDialog.student.id ? { ...s, photoURL: url } : s));
            setStudents(updater);
            setFilteredStudents(updater);

            setPhotoDialog({ open: false, student: null });
            Swal.fire("Saved", "Student photo updated", "success");
        } catch (e) {
            Swal.fire("Error", "Upload failed", "error");
        } finally {
            setUploading(false);
            setIsWebcam(false);
        }
    };

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" mb={2} spacing={2} alignItems="center">
                <Box>
                    <Typography variant="h6" fontWeight="900" color="error">
                        Cancelled Admissions Directory
                    </Typography>
                    <Chip
                        label={`Total Cancelled: ${filteredStudents.length}`}
                        size="small"
                        color="error"
                        sx={{ fontWeight: "bold" }}
                    />
                </Box>
                <Stack direction="row" spacing={1}>
                    <TextField
                        select
                        size="small"
                        label="Filter by Class"
                        value={selectedClass}
                        onChange={(e) => handleFilter(search, e.target.value)}
                        sx={{ width: 180 }}
                    >
                        <MenuItem value="All">All Classes</MenuItem>
                        {classes.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        placeholder="Search Name, Roll, GR or Mobile..."
                        size="small"
                        value={search}
                        onChange={(e) => handleFilter(e.target.value, selectedClass)}
                        sx={{ width: 280 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            )
                        }}
                    />
                </Stack>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                {loading ? (
                    <Box p={10} textAlign="center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Sr.</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Photo</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Roll</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Student Name</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Class</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Gender</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Type</TableCell>
                                <TableCell sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Primary Mobile</TableCell>
                                <TableCell align="right" sx={{ bgcolor: "#f8f9fa", fontWeight: "bold" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredStudents
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((s, index) => (
                                    <TableRow key={s.id} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>
                                            <Avatar
                                                src={s.photoURL}
                                                onClick={() => setPhotoDialog({ open: true, student: s })}
                                                sx={{ width: 40, height: 40, cursor: "pointer", border: "1px solid #ddd" }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "primary.main" }}>
                                            {s.rollNo}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="600">
                                                {s.firstname || s.name} {s.fathername || ""} {s.surname || ""}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={s.className} size="small" variant="outlined" color="primary" />
                                        </TableCell>
                                        <TableCell>{s.gender || "-"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={s.studenttype}
                                                size="small"
                                                color={s.studenttype === "New" ? "success" : "default"}
                                            />
                                        </TableCell>
                                        <TableCell>{s.fatherMobile}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="View Profile">
                                                    <IconButton size="small" color="info" onClick={() => navigate(`/student-profile/${s.id}`)}>
                                                        <Visibility fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit Admission">
                                                    <IconButton size="small" color="warning" onClick={() => navigate(`/admission/${s.id}`)}>
                                                        <Edit fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Restore Admission">
                                                    <IconButton size="small" color="success" onClick={() => handleRestoreAdmission(s)}>
                                                        <Replay fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Permanent Delete">
                                                    <IconButton size="small" color="error" onClick={() => handlePermanentDelete(s)}>
                                                        <DeleteForever fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {filteredStudents.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                        No cancelled students found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={filteredStudents.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>

            <Dialog
                open={photoDialog.open}
                onClose={() => {
                    setPhotoDialog({ open: false, student: null });
                    setIsWebcam(false);
                }}
            >
                <DialogTitle>Update Student Photo</DialogTitle>
                <DialogContent sx={{ textAlign: "center", minWidth: 320 }}>
                    {isWebcam ? (
                        <Box sx={{ border: "2px solid teal", borderRadius: 2, overflow: "hidden", mb: 2 }}>
                            <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" width="100%" />
                        </Box>
                    ) : (
                        <Avatar
                            src={photoDialog.student?.photoURL}
                            sx={{ width: 120, height: 120, mx: "auto", my: 2, border: "2px solid #ddd" }}
                        />
                    )}
                    <Stack spacing={1.5}>
                        {isWebcam ? (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PhotoCamera />}
                                onClick={() => uploadPhoto(webcamRef.current.getScreenshot(), "url")}
                            >
                                Take Screenshot
                            </Button>
                        ) : (
                            <Button variant="outlined" startIcon={<PhotoCamera />} onClick={() => setIsWebcam(true)}>
                                Use Webcam
                            </Button>
                        )}
                        <Button variant="outlined" color="secondary" component="label" startIcon={<FileUpload />}>
                            Upload from Gallery
                            <input type="file" hidden accept="image/*" onChange={(e) => uploadPhoto(e.target.files[0], "file")} />
                        </Button>
                        <Button
                            onClick={() => {
                                setPhotoDialog({ open: false, student: null });
                                setIsWebcam(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                    {uploading && <CircularProgress size={24} sx={{ mt: 2 }} />}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default Cancellist;